/**
 * PII_CLASSIFICATION registry — the at-rest treatment class for every column of
 * packages/server/db/schema.ts (ADR-0019 Option C, decided 2026-07-16; TASK-00027
 * stage 1). Loads schema/pii-classification.json (single canonical source per
 * ADR-0017), Zod-validates at module load, FAIL CLOSED.
 *
 * The classes are a CLOSED union:
 *   identifier-kms     — workflow identifiers and student-linked operational
 *                        attributes (names, SSID, dob, parent contact,
 *                        demographics used in aggregate reporting, clock dates).
 *                        Server-operable; KMS field-level encryption at rest;
 *                        may be indexed (index mechanics under encryption are
 *                        the schema-alignment stage's decision).
 *   sensitive-envelope — evaluation narratives, disability determinations,
 *                        health details, document bodies. Client-side envelope;
 *                        the server never sees plaintext; NEVER btree-indexed.
 *                        An indexed sensitive-envelope entry is UNREPRESENTABLE.
 *   non-pii            — structural machinery: surrogate keys, tenancy scoping,
 *                        system timestamps, integrity digests, catalogs.
 *
 * Rule 2 (fail closed) is the load-bearing design:
 *   - classify() of a field the registry does not know returns
 *     'sensitive-envelope': an UNCLASSIFIED field gets the STRONGEST treatment,
 *     never none. There is no way to fall through to "no encryption".
 *   - the loader throws on any malformed entry, duplicate path, shrunken
 *     minFields floor, or floor member classified weaker than the floor demands
 *     (the export-sections ferpaDerivation pattern: a floor proves the
 *     classification can fail; it never defines the set).
 *   - assertNoIndexOnEnvelope() refuses an EMPTY field list — an index audit
 *     over nothing certifies nothing.
 *
 * Consumed by the schema-shape gate lint (a later TASK-00027 stage), which
 * derives the real column/index set from schema.ts and holds it to this
 * registry via classify() / isEnvelopeClass() / assertNoIndexOnEnvelope().
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import piiClassificationJson from '../../../../schema/pii-classification.json'

export const PII_CLASSES = ['identifier-kms', 'sensitive-envelope', 'non-pii'] as const
export const PiiClassSchema = z.enum(PII_CLASSES)
export type PiiClass = z.infer<typeof PiiClassSchema>

/** The fail-closed default: an unclassified field gets the STRONGEST treatment. */
export const PII_UNCLASSIFIED_DEFAULT = 'sensitive-envelope' as const satisfies PiiClass

export const PiiFieldEntrySchema = z
  .object({
    fieldPath: z
      .string()
      .regex(
        /^[A-Za-z][A-Za-z0-9]*\.[A-Za-z][A-Za-z0-9]*$/,
        "fieldPath must be '<tableExport>.<propertyName>' as written in packages/server/db/schema.ts",
      ),
    column: z.string().min(1),
    class: PiiClassSchema,
    indexed: z.boolean(),
    rationale: z.string().min(1),
  })
  .strict()
  .superRefine((entry, ctx) => {
    if (entry.class === 'sensitive-envelope' && entry.indexed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `field '${entry.fieldPath}': sensitive-envelope with indexed:true is UNREPRESENTABLE — a btree index requires server-readable values, and the envelope class promises the server never has them`,
      })
    }
  })

export type PiiFieldEntry = z.infer<typeof PiiFieldEntrySchema>

const PiiClassificationFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    adr: z.string().min(1),
    roadmapNote: z.string(),
    classes: z.record(z.string()),
    // The fail-closed default is pinned as data AND as code: weakening it in the
    // JSON is unrepresentable here, and weakening it here breaks the literal type.
    defaultClass: z.literal(PII_UNCLASSIFIED_DEFAULT),
    defaultRationale: z.string().min(1),
    recordedDrift: z.array(z.string().min(1)),
    contracts: z
      .object({
        minFields: z.number().int().min(1),
        note: z.string().min(1),
        envelopeFloor: z.array(z.string().min(1)).min(1),
        identifierFloor: z.array(z.string().min(1)).min(1),
      })
      .strict(),
    fields: z.array(PiiFieldEntrySchema).min(1),
  })
  .strict()

interface LoadedPiiClassification {
  fields: readonly PiiFieldEntry[]
  index: ReadonlyMap<string, PiiFieldEntry>
}

/**
 * Validate a pii-classification file. Exported so tests can prove the loader
 * REJECTS decoys (Rule 3: every guard must be provable to fail). THROWS on any
 * violation — the registry never degrades to a partial load.
 */
export function parsePiiClassificationFile(data: unknown): LoadedPiiClassification {
  const parsed = PiiClassificationFileSchema.safeParse(data)
  if (!parsed.success) {
    throw AppError.validation(
      'PII_CLASSIFICATION registry failed validation — refusing to load a malformed field classification (fail closed)',
      parsed.error.issues,
    )
  }
  const { classes, contracts, fields } = parsed.data

  // The class vocabulary in the data file must be exactly the closed union.
  const declared = Object.keys(classes).sort()
  const canonical = [...PII_CLASSES].sort()
  if (JSON.stringify(declared) !== JSON.stringify(canonical)) {
    throw AppError.validation(
      `PII_CLASSIFICATION class vocabulary drifted: file declares [${declared.join(', ')}], canon is [${canonical.join(', ')}]`,
    )
  }

  const index = new Map<string, PiiFieldEntry>()
  for (const entry of fields) {
    if (index.has(entry.fieldPath)) {
      throw AppError.validation(
        `PII_CLASSIFICATION registry: duplicate fieldPath '${entry.fieldPath}' — two classifications for one column is no classification`,
      )
    }
    index.set(entry.fieldPath, entry)
  }

  // Ratchet floor: the classification may only shrink deliberately.
  if (fields.length < contracts.minFields) {
    throw AppError.validation(
      `PII_CLASSIFICATION registry: ${fields.length} field entries, contracts floor is ${contracts.minFields} — the classification shrank (restore the data, or lower the floor deliberately and say why)`,
    )
  }

  // Envelope floor: every member must be present and classified EXACTLY
  // sensitive-envelope. Weakening a floor member is the defect class this
  // floor exists to catch; the floor proves the classification can fail.
  for (const path of contracts.envelopeFloor) {
    const entry = index.get(path)
    if (!entry) {
      throw AppError.validation(
        `PII_CLASSIFICATION registry: envelopeFloor member '${path}' is not classified — a floor member outside the set means the classification can no longer prove itself`,
      )
    }
    if (entry.class !== 'sensitive-envelope') {
      throw AppError.validation(
        `PII_CLASSIFICATION registry: envelopeFloor member '${path}' is classified '${entry.class}' — weakening an envelope-floor field (disability/health/evaluation substance, document bodies) is exactly what this floor exists to refuse`,
      )
    }
  }

  // Identifier floor: every member must be present and must never be non-pii.
  for (const path of contracts.identifierFloor) {
    const entry = index.get(path)
    if (!entry) {
      throw AppError.validation(
        `PII_CLASSIFICATION registry: identifierFloor member '${path}' is not classified — the legacy FERPA gate missed identifiers that were on no list; this floor refuses that class of omission`,
      )
    }
    if (entry.class === 'non-pii') {
      throw AppError.validation(
        `PII_CLASSIFICATION registry: identifierFloor member '${path}' is classified 'non-pii' — a known person identifier declassified to plaintext-at-rest is a compliance defect, not a tuning choice`,
      )
    }
  }

  return { fields: Object.freeze(parsed.data.fields), index }
}

const loaded = parsePiiClassificationFile(piiClassificationJson)

/** Every classified field, validated. Frozen — the registry is read-only at runtime. */
export const PII_CLASSIFICATION: readonly PiiFieldEntry[] = loaded.fields

const PII_FIELD_INDEX: ReadonlyMap<string, PiiFieldEntry> = loaded.index

/** Whether a field path has an explicit classification entry. */
export function hasPiiClassification(fieldPath: string): boolean {
  return PII_FIELD_INDEX.has(fieldPath)
}

/**
 * Resolve the full classification ENTRY for a field path. THROWS on unknown —
 * classify() is the fail-closed total function; resolving an entry that does
 * not exist is a build defect, not a default.
 */
export function getPiiClassification(fieldPath: string): PiiFieldEntry {
  const entry = PII_FIELD_INDEX.get(fieldPath)
  if (!entry) {
    throw AppError.notFound(
      `Unknown field path '${fieldPath}' in the PII_CLASSIFICATION registry — use classify() for the fail-closed class of an unclassified field; resolving its ENTRY is a build defect`,
    )
  }
  return entry
}

/**
 * The at-rest treatment class for a field path. TOTAL and FAIL CLOSED: a field
 * the registry does not know returns 'sensitive-envelope' — an unclassified
 * field gets the STRONGEST treatment, never none.
 */
export function classify(fieldPath: string): PiiClass {
  const entry = PII_FIELD_INDEX.get(fieldPath)
  return entry ? entry.class : PII_UNCLASSIFIED_DEFAULT
}

/** Whether a field path classifies as sensitive-envelope (unknown fields DO — fail closed). */
export function isEnvelopeClass(fieldPath: string): boolean {
  return classify(fieldPath) === 'sensitive-envelope'
}

/**
 * Assert that no envelope-class field in the given set is indexed. For the
 * schema-shape lint: pass EVERY column parsed from schema.ts with its real
 * index participation.
 *
 * FAIL CLOSED twice over:
 *   - an EMPTY input throws — an index audit over zero fields certifies
 *     nothing (a schema parse that found no columns is a broken parse, not a
 *     clean schema)
 *   - an UNKNOWN indexed field throws — it defaults to sensitive-envelope,
 *     and envelope is never indexable, so a brand-new indexed column fails the
 *     gate until it is deliberately classified
 */
export function assertNoIndexOnEnvelope(
  fields: ReadonlyArray<{ fieldPath: string; indexed: boolean }>,
): void {
  if (fields.length === 0) {
    throw AppError.validation(
      'assertNoIndexOnEnvelope: ZERO fields provided — an index audit over nothing certifies nothing (fail closed); the caller must pass every column it parsed',
    )
  }
  const violations = fields
    .filter((field) => field.indexed && isEnvelopeClass(field.fieldPath))
    .map(
      (field) =>
        `${field.fieldPath} (${
          hasPiiClassification(field.fieldPath)
            ? 'classified sensitive-envelope'
            : 'UNCLASSIFIED — defaults to sensitive-envelope, fail closed'
        })`,
    )
  if (violations.length > 0) {
    throw AppError.validation(
      `assertNoIndexOnEnvelope: ${violations.length} indexed envelope-class field(s) — a btree index requires server-readable values, and the envelope class promises the server never has them: ${violations.join('; ')}`,
      { violations },
    )
  }
}
